<script setup>
import {ArrowDownIcon, DuplicateIcon, FlagIcon, TrashIcon, XIcon} from "@heroicons/vue/outline";
import {ElButton, ElCheckbox, ElDropdown, ElDropdownItem, ElDropdownMenu, ElTooltip} from "element-plus";
import ActionHistoryHstSyncStatus from "@/Components/ActionHistoryHstSyncStatus.vue";
import {computed, onMounted, ref} from "vue";
import moment from "moment-timezone";
import {Inertia} from "@inertiajs/inertia";
import Swal from "sweetalert2";
import LoadingSpinner from "@/Components/LoadingSpinner.vue";
import {usePage} from "@inertiajs/inertia-vue3";

const props = defineProps({
    roles: {},
    lead: Object,
    nextLeadScheduleList: {
        type: Array,
        required: true,
    },
    leadActionHistory: Array,
    hstActionSyncStatuses: {},
})

const pageProps = usePage().props.value;

const emit = defineEmits(['performExtraOption', 'showEmailMessageContent', 'showEmailTemplateContent', 'showGracePullDetails']);

const moment_wrapper = moment;
const filterActions = ref({
    texts: {
        label: 'Texts',
        value: true,
    },
    emails: {
        label: 'Emails',
        value: true,
    },
    calls: {
        label: 'Calls',
        value: true,
    },
    notes: {
        label: 'Notes',
        value: true,
    },
    other: {
        label: 'Other',
        value: true,
    }
})

const pinnedNotes = computed(() => {
  if (props.lead.pinned_lead_action_history_ids?.length) {
      return props.leadActionHistory?.filter(item => props.lead.pinned_lead_action_history_ids.includes(item.id) && item.act_name === 'Note');
  }
  return null;
})
const filterActionsShowEverything = computed(() => {
    for (let prop in filterActions.value) {
        if (!filterActions.value.hasOwnProperty(prop)) continue;
        filterActions.value[prop].value = true;
    }
})
const filterActionsFilteringString = computed(() => {
    let showingEverything = true;
    let showing = [];

    for (let prop in filterActions.value) {
        if (!filterActions.value.hasOwnProperty(prop)) continue;
        if (!filterActions.value[prop].value) showingEverything = false;
        if (filterActions.value[prop].value) {
            showing.push(filterActions.value[prop].label);
        }
    }

    if (showingEverything) {
        return 'All';
    } else {
        return showing.join(', ');
    }
})


const togglePinLeadHistoryEntry = function (leadHistoryEntry) {
    if (leadHistoryEntry.pinned) {
        unpinLeadHistoryEntry(leadHistoryEntry);
    } else {
        pinLeadHistoryEntry(leadHistoryEntry);
    }
}
const pinLeadHistoryEntry = function(leadHistoryEntry) {
    Inertia.put(route('lead-action.history.pin', leadHistoryEntry.id), {}, {
        only: ['leadActionHistory'],
        preserveScroll: true,
        onSuccess: () => {
            handleActionPinnedSuccess();
        },
        onError: (e) => {
            throw e;
        }
    })
}
const unpinLeadHistoryEntry = function(leadHistoryEntry) {
    Inertia.put(route('lead-action.history.unpin', leadHistoryEntry.id), {}, {
        only: ['leadActionHistory'],
        preserveScroll: true,
        onSuccess: () => {
            handleActionUnpinnedSuccess();
        },
        onError: (e) => {
            throw e;
        }
    })
}

const handleActionPinnedSuccess = function() {
    $notify.success({
        title: 'Success',
        message: 'Note has been pinned',
        showClose: true,
    });
}

const handleActionUnpinnedSuccess = function() {
    $notify.success({
        title: 'Success',
        message: 'Note has been unpinned',
        showClose: true,
    });
}

// Intake form link helpers
const intakeFormLinkRegex = /\{\{intake_form_link:(.*?)\}\}/;

const hasIntakeFormLink = (notes) => {
    return intakeFormLinkRegex.test(notes);
};

const getIntakeFormLink = (notes) => {
    const match = notes.match(intakeFormLinkRegex);
    return match ? match[1] : '#';
};

const getNotesWithoutLink = (notes) => {
    return notes.replace(intakeFormLinkRegex, '').trim();
};

const visibleLeadActionHistory = computed(() => {
    if (!props.leadActionHistory) return [];
    return props.leadActionHistory
        .filter(actionHistoryItem => {
            // Exclude pinned notes from the completed actions table
            if (actionHistoryItem.pinned && actionHistoryItem.act_name === 'Note') {
                return false;
            }

            return actionHistoryItem.action || actionHistoryItem.dynamic || actionHistoryItem.always_show;
        })
        .filter(actionHistoryItem => {
            if (filterActions.value.emails.value &&
                filterActions.value.texts.value &&
                filterActions.value.calls.value &&
                filterActions.value.notes.value &&
                filterActions.value.other.value) {
                return true;
            }

            let act_name = actionHistoryItem.act_name?.toLowerCase();
            if (act_name === 'note') {
                return filterActions.value.notes.value;
            }
            if (act_name === 'sent text' || act_name === 'sent custom text' || act_name === 'received text') {
                return filterActions.value.texts.value;
            }
            if (act_name === 'sent email' || act_name === 'sent custom email') {
                return filterActions.value.emails.value;
            }
            if (act_name?.substring(0, 4) === 'call') {
                return filterActions.value.calls.value;
            }
            return filterActions.value.other.value;
        })
        .filter(actionHistoryItem => {
            if (actionHistoryItem.completed_notes?.substring(0, 19) === 'Lead Source changed'
                && !['Super Admin', 'Agency Admin'].includes(role)) {
                return false;
            }
            return true;
        })
        .filter(actionHistoryItem => {
            if (actionHistoryItem.completed_notes?.substring(0, 19) === 'Status changed'
                && !['Super Admin', 'Agency Admin', 'Recruiter Admin'].includes(role)) {
                return false;
            }
            return true;
        });
})

const isAdmin = computed(() => {
  return props.roles.some(i => ['Super Admin', 'Agency Admin'].indexOf(i.name) > -1);
})
const canPerformActions = computed(() => {
    return props.roles?.length > 1 || !props.roles.some(i => i.name === 'Referral Program User');
})



const flagSchedule = function(leadSchedule) {
    Inertia.put(route('lead-action.schedule.flag', leadSchedule.id), {}, {
        only: ['nextLeadScheduleList'],
        preserveScroll: true,
        onSuccess: () => {
            handlActionFlaggedSuccess();
        }
    })
}

const flagLeadHistoryEntry = function(leadHistoryEntry) {
    Inertia.put(route('lead-action.history.flag', leadHistoryEntry.id), {}, {
        only: ['leadActionHistory'],
        preserveScroll: true,
        onSuccess: () => {
            handlActionFlaggedSuccess();
        },
        onError: (e) => {
            throw e;
        }
    })
}
const deleteLeadHistoryEntry = function(leadHistoryEntry) {
    Swal.fire({
        title: 'Delete this lead history entry?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#B91C1C',
        cancelButtonColor: '#899CC1'
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.delete(route('lead-action.history.delete', leadHistoryEntry.id), {
                only: ['leadActionHistory'],
                preserveScroll: true,
                onSuccess: () => {
                    handleActionHistoryNoteDeletedSuccess();
                },
                onError: (e) => {
                    throw e;
                }
            })
        }
    });
}
const deleteSchedule = function(leadScheduule) {
    Swal.fire({
        title: 'Delete this lead\'s scheduled action?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#B91C1C',
        cancelButtonColor: '#899CC1'
    }).then((result) => {
        if (result.isConfirmed) {
            Inertia.delete(route('lead-action.schedule.delete', leadScheduule.id), {
                only: ['nextLeadScheduleList'],
                preserveScroll: true,
                onSuccess: () => {
                    handleActionHistoryNoteDeletedSuccess();
                },
                onError: (e) => {
                    throw e;
                }
            })
        }
    });
}
const unflagSchedule = function(leadSchedule) {
    Inertia.put(route('lead-action.schedule.unflag', leadSchedule.id), {}, {
        only: ['nextLeadScheduleList'],
        preserveScroll: true,
        onSuccess: () => {
            handlActionUnflaggedSuccess();
        },
        onError: (e) => {
            throw e;
        }
    })
}
const unflagLeadHistoryEntry = function(leadHistoryEntry) {
    Inertia.put(route('lead-action.history.unflag', leadHistoryEntry.id), {}, {
        only: ['leadActionHistory'],
        preserveScroll: true,
        onSuccess: () => {
            handlActionUnflaggedSuccess();
        },
        onError: (e) => {
            throw e;
        }
    })
}

const handleActionHistoryNoteDeletedSuccess = function() {
    $notify.success({
        title: 'Success',
        message: 'Action deleted',
        showClose: true,
    });
}

const tabLoading = ref(false);
onMounted(() => {
    if (props.leadActionHistory?.length && props.nextLeadScheduleList?.length) {
        return;
    }
    tabLoading.value = true;
    Inertia.reload({
        only: ['leadActionHistory'],
        preserveState: true,
        preserveScroll: true,
        onFinish() {
            tabLoading.value = false;
        }
    })
})

const role = computed(() => {
    return props.roles.find(role => ['Super Admin', 'Agency Admin', 'Recruiter Admin', 'Agent'].includes(role.name));
})

const performExtraOption = function(extraOption) {
    emit('performExtraOption', extraOption, ['leadActionHistory']);
}

const showEmailMessageContent = (emailMessageId) => {
  emit('showEmailMessageContent', emailMessageId);
}

const showEmailTemplateContent = (emailTemplateId) => {
  emit('showEmailTemplateContent', emailTemplateId);
}

const showGracePullDetails = (gracePullLogId) => {
  emit('showGracePullDetails', gracePullLogId);
}



let handlActionFlaggedSuccess = function() {
    $notify.success({
        title: 'Success',
        message: 'Action has been flagged',
        showClose: true,
    });
}
let handlActionUnflaggedSuccess = function() {
    $notify.success({
        title: 'Success',
        message: 'Action unflagged',
        showClose: true,
    });
}

const shouldShowHistoryLeadTimezone = function(actionHistoryItem) {
    if ( ! actionHistoryItem.completed_at) {
        return false;
    }
    if ( ! props.lead.timezone ) {
        return false;
    }

    if ( props.lead.timezone && pageProps.auth.timezone ) {
        if ( props.lead.timezone === pageProps.auth.timezone ) {
            return false;
        }
    } else if ( props.lead.timezone_region_name && pageProps.auth.timezone_region_name ) {
        if ( props.lead.timezone_region_name === pageProps.auth.timezone_region_name ) {
            return false;
        }
    }

    const act_name = actionHistoryItem?.act_name?.toLowerCase();

    return ['sent text', 'sent custom text', 'received text', 'sent email', 'sent custom email'].indexOf(act_name) >= 0;
}

const leadsTimezoneRegionName = computed(function(){
    if (props.lead.timezone_region_name) {
        return props.lead.timezone_region_name;
    }
    return 'Lead Time';
});

const timezone = usePage().props.value?.auth?.timezone;
</script>

<template>
<div>
  <div v-if="tabLoading" class="relative w-full">
    <div class="p-8 shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white flex items-center justify-center">
      <LoadingSpinner color-class="text-tb-blue w-8 h-8"/>
    </div>
  </div>
  <div v-else>
    <div class="overflow-x-auto rounded-lg border bg-tb-blue mb-4" v-if="pinnedNotes?.length">
      <h1 class="px-2 py-2 text-white text-md font-bold">Pinned Notes</h1>
      <div v-for="(pinnedNote, index) in pinnedNotes" :key="pinnedNote.id"  class="bg-blue-100 p-4 border-t border-gray-300">
        <div class="flex justify-between items-start">
          <div class="flex-grow">
            <div class="text-xs text-gray-900">
              <span class="font-semibold">{{ pinnedNote.user_shorthand }}</span> -
              {{ moment_wrapper(pinnedNote.completed_at).format('M/D/Y h:mm A') }}
            </div>
            <div class="mt-2 text-sm whitespace-pre-wrap">{{ pinnedNote.completed_notes }}</div>
          </div>
          <button class="btn btn-sm btn-secondary" @click="unpinLeadHistoryEntry(pinnedNote)">
            <XIcon class="w-4" />
          </button>
        </div>
      </div>
    </div>
    <div class="mb-2 text-right">
      <ElDropdown trigger="hover" :hide-on-click="false">
        <ElButton color="#899CC1">
          <span class="text-white">Show: {{ filterActionsFilteringString }}</span> <ArrowDownIcon class="text-white pl-2 w-4" />
        </ElButton>
        <template #dropdown>
          <ElDropdownMenu>
            <div id="filtersDropdown">
              <ElDropdownItem @click="filterActionsShowEverything">Show All</ElDropdownItem>
              <ElDropdownItem><ElCheckbox v-model="filterActions.texts.value" label="Texts" /></ElDropdownItem>
              <ElDropdownItem><ElCheckbox v-model="filterActions.emails.value" label="Emails" /></ElDropdownItem>
              <ElDropdownItem><ElCheckbox v-model="filterActions.calls.value" label="Calls" /></ElDropdownItem>
              <ElDropdownItem><ElCheckbox v-model="filterActions.notes.value" label="Notes" /></ElDropdownItem>
              <ElDropdownItem><ElCheckbox v-model="filterActions.other.value" label="Other" /></ElDropdownItem>
            </div>
          </ElDropdownMenu>
        </template>
      </ElDropdown>
    </div>
    <div class="overflow-x-auto rounded-lg border bg-tb-blue mt-4">
      <h1 class="px-2 py-2 text-white text-md font-bold">Incomplete Actions</h1>
      <table class="min-w-full divide-y divide-gray-300 border-t border-t-gray-300">
        <thead class="bg-slate-200">
        <tr>
          <th scope="col" class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900">Date/Time Scheduled</th>
          <th scope="col" class="hitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Act</th>
          <th scope="col" class="hitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Note</th>
          <th v-if="canPerformActions" scope="col" class="hitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Flag Action</th>
        </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
        <tr v-if="nextLeadScheduleList.length === 0"><td colspan="5" class="whitespace-nowrap px-2 py-3.5  text-xs text-gray-900 text-center">No Action</td></tr>
        <tr v-else v-for="nextLeadSchedule in nextLeadScheduleList" :key="nextLeadScheduleList.id" :class="{
                  'bg-yellow-200': nextLeadSchedule.flagged
                }">
          <td class="whitespace-nowrap px-2 py-3.5 text-xs text-gray-900 cursor-[help]" style="vertical-align: top;">
            <el-tooltip placement="top">
              <template #content>
                Date/Time Created: {{ moment_wrapper(nextLeadSchedule.created_at).tz(timezone).format('M/D/Y h:mm A') }}
              </template>
              {{ moment_wrapper(nextLeadSchedule.scheduled_datetime).tz('America/New_York').format('M/D/Y h:mm A') }}
            </el-tooltip>
          </td>
          <td class="whitespace-nowrap px-2 py-3.5 text-xs text-gray-900" style="vertical-align: top;">{{nextLeadSchedule.action?.is_task ? 'Task:' : null }} {{ nextLeadSchedule.action?.name }}</td>
          <td class="break-words px-2 py-3.5  text-xs text-gray-900">
            <p>{{ nextLeadSchedule.user_shorthand }}</p>
            <p>{{ nextLeadSchedule.notes }}</p>
            <p v-if="nextLeadSchedule.hst_apptid" class="text-green-600 font-semibold mt-1">Appointment synced to HST (Appt ID {{ nextLeadSchedule.hst_apptid }})</p>
          </td>
          <td v-if="canPerformActions" class="whitespace-nowrap px-2 py-3.5  text-xs text-gray-900" style="vertical-align: top;">
            <button v-if="!nextLeadSchedule.flagged" class="btn btn-sm btn-secondary mr-2" @click="flagSchedule(nextLeadSchedule)"><FlagIcon class="w-4" /></button>
            <button v-else class="btn btn-sm btn-secondary mr-2" @click="unflagSchedule(nextLeadSchedule)"><XIcon class="w-4" /><FlagIcon class="w-4" /></button>
            <button v-if="isAdmin || lead.allow_delete_notes == 1" class="btn btn-xs btn-secondary" @click="deleteSchedule(nextLeadSchedule)"><TrashIcon class="w-4" /></button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="mt-8 overflow-x-auto rounded-lg border bg-tb-blue">
      <h1 class="px-2 py-2 text-white text-md font-bold">Completed Actions</h1>
      <table class="min-w-full divide-y divide-gray-300 border-t border-t-gray-300">
        <thead class="bg-slate-200">
        <tr>
          <th scope="col" class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900">Date/Time Completed</th>
          <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Act</th>
          <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Note</th>
          <th v-if="canPerformActions" scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Options</th>
        </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
        <tr v-if="visibleLeadActionHistory.length" v-for="(actionHistoryItem, index) in visibleLeadActionHistory" :key="`lah_${actionHistoryItem.id}`" :class="{
                            'bg-yellow-200': actionHistoryItem.flagged,
                            'bg-blue-100': lead.pinned_lead_action_history_ids.includes(actionHistoryItem.id)
                      }">
          <td class="whitespace-nowrap px-2 py-3.5  text-xs text-gray-900" style="vertical-align: top;">
            <div class="cursor-[help]">
              <el-tooltip placement="top">
                <template #content>
                  <p>Date/Time Created: {{ moment_wrapper(actionHistoryItem.completed_lead_schedule ? actionHistoryItem.completed_lead_schedule.created_at : actionHistoryItem.created_at).tz(timezone).format('M/D/Y h:mm A') }}</p>
                </template>
                {{ moment_wrapper(actionHistoryItem.completed_at).tz(timezone).format('M/D/Y h:mm A') }}
              </el-tooltip>
            </div>
            <div v-if="shouldShowHistoryLeadTimezone(actionHistoryItem)" class="mt-2">
              {{ moment_wrapper(actionHistoryItem.completed_at).tz(lead.timezone).format('M/D/Y h:mm A') }}
              <span class="bg-blue-500 text-white rounded-xl ml-2 px-2 opacity-60">
                {{ leadsTimezoneRegionName }}
              </span>
            </div>
          </td>
          <td class="whitespace-nowrap px-2 py-3.5  text-xs text-gray-900" style="vertical-align: top;">
            <span v-if="actionHistoryItem.action?.is_task">Task: </span> {{ actionHistoryItem.act_name }}
          </td>
          <td class="px-2 py-3.5 text-xs text-gray-900 max-w-[300px] xl:max-w-[400px] overflow-auto" style="vertical-align: top;">
            {{ actionHistoryItem.user_shorthand }} <br v-if="actionHistoryItem.user_shorthand" />
            <div v-if="actionHistoryItem.completed_notes" class="whitespace-pre-wrap">
              <template v-if="hasIntakeFormLink(actionHistoryItem.completed_notes)">
                {{ getNotesWithoutLink(actionHistoryItem.completed_notes) }}
                <a :href="getIntakeFormLink(actionHistoryItem.completed_notes)" class="text-blue-600 underline hover:text-blue-800 block mt-1 cursor-pointer" @click.prevent="Inertia.visit(getIntakeFormLink(actionHistoryItem.completed_notes))">
                  View Submission
                </a>
              </template>
              <template v-else>{{ actionHistoryItem.completed_notes }}</template>
            </div>
            <div v-if="actionHistoryItem.to_email">
              To: {{ actionHistoryItem.to_email }}
            </div>
            <div v-if="actionHistoryItem.notes_title">
              <a class="text-blue-600 underline" v-if="actionHistoryItem.email_message_id" @click.prevent="showEmailMessageContent(actionHistoryItem.email_message_id)" href="javascript;">{{ actionHistoryItem.notes_title }}</a>
              <a class="text-blue-600 underline" v-else-if="actionHistoryItem.email_template_id" @click.prevent="showEmailTemplateContent(actionHistoryItem.email_template_id)" href="javascript;">{{ actionHistoryItem.notes_title }}</a>
              <span v-else>{{ actionHistoryItem.notes_title }}</span>
            </div>
            <div v-if="isAdmin && actionHistoryItem.grace_pull_log_id">
              <a class="text-blue-600 underline" @click.prevent="showGracePullDetails(actionHistoryItem.grace_pull_log_id)" href="javascript;">
                Grace Pull Details
              </a>
            </div>

            <ActionHistoryHstSyncStatus v-if="actionHistoryItem.id"
                                        :action-history-item-id="actionHistoryItem.id"
                                        :hst-action-sync-statuses="hstActionSyncStatuses"
            ></ActionHistoryHstSyncStatus>
          </td>
          <td v-if="canPerformActions" class="whitespace-nowrap px-2 py-3.5  text-xs text-gray-900" style="vertical-align: top;">
            <div v-if="!actionHistoryItem.dynamic">
              <button v-if="!actionHistoryItem.flagged" class="btn btn-sm btn-secondary mr-2" @click="flagLeadHistoryEntry(actionHistoryItem)"><FlagIcon class="w-4" /></button>
              <button v-else class="btn btn-sm btn-secondary mr-2" @click="unflagLeadHistoryEntry(actionHistoryItem)"><XIcon class="w-4" /><FlagIcon class="w-4" /></button>
              <button v-if="actionHistoryItem.act_name === 'Note'" class="btn btn-sm btn-secondary mr-2" @click="togglePinLeadHistoryEntry(actionHistoryItem)" :title="actionHistoryItem.pinned ? 'Unpin' : 'Pin'">
                <template v-if="!actionHistoryItem.pinned">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 2L12 22"></path>
                    <path d="M5 12H19"></path>
                  </svg>
                </template>
                <template v-else>
                  <XIcon class="w-4" />
                </template>
              </button>
              <button v-if="isAdmin || lead.allow_delete_notes == 1" class="btn btn-xs btn-secondary" @click="deleteLeadHistoryEntry(actionHistoryItem)"><TrashIcon class="w-4" /></button>
            </div>
            <div v-else>
              <button v-for="extraOption in actionHistoryItem.extra_options" class="btn btn-sm btn-secondary focus:ring-0 mr-2" @click.prevent="performExtraOption(extraOption)">
                <DuplicateIcon class="w-4 mr-2" />
                <span class="text-xs">{{ extraOption.label }}</span>
              </button>
            </div>
          </td>
        </tr>
        <tr v-else>
          <td colspan="6" class="whitespace-nowrap px-2 py-3.5  text-xs text-gray-900 text-center">No data</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
</template>